import {Injectable} from '@angular/core';
import {_DEFAULT_NUTRITIONAL_PROPERTIES, IFoodGroupIngredient, INutritionalProperties} from '../../models/food-group';
import {IIngredient} from '../../models/ingredient';
import {cloneDeep} from 'lodash';
import {IMeasurementUnit} from '../../models/measurement';

@Injectable({
  providedIn: 'root'
})
export class IngredientCalculationService {
  // tslint:disable:no-redundant-jsdoc

  constructor() { }

  /**
   * Calcula la suma dada una lista de ingredientes
   * @param ingredients arreglo de ingredientes
   * @returns propiedades nutricionales
   */
  calculateIngredientListNutritionalProperties(
    ingredients: Partial<IFoodGroupIngredient>[]
  ): INutritionalProperties {
    const properties = cloneDeep(_DEFAULT_NUTRITIONAL_PROPERTIES);
    // Sumar las cantidades de cada ingrediente del usuario (para cada propiedad nutricional)
    Object.keys(properties).forEach(key => {
      properties[key] = ingredients.reduce((sum, current) => {
        if (current?.nutritionalProperties[key] !== -1) {
          return +(sum + current?.nutritionalProperties[key]).toFixed(2);
        } else {
          return 0;
        }
      }, 0);
    });
    return properties;
  }

  /**
   * Calcula las propiedades nutricionales (dado el ingrediente seleccionado)
   * @param ingredient ingrediente seleccionado
   * @param measureUnits arreglo de unidades de medida
   * @param selectedMeasureUnitId id unidad de medida
   * @param units cantidad en unidades (número entero)
   * @returns el objeto del ingrediente con sus propiedades calculadas
   */
  calculateNutritionalProperties(
    ingredient: IIngredient,
    measureUnits: IMeasurementUnit[],
    selectedMeasureUnitId: string,
    units: number
  ): Partial<INutritionalProperties> {
    // Primero ver la unidad de medida seleccionada
    const nutritionalProperties: Partial<INutritionalProperties> = cloneDeep(_DEFAULT_NUTRITIONAL_PROPERTIES);
    // Verificar si se elige porción
    if (selectedMeasureUnitId === 'porcion') {
      // Si es así se hace la multiplicación directa por las unidades
      Object.keys(ingredient).forEach(key => {
        if (nutritionalProperties[key] !== undefined) {
          if (ingredient[key] !== -1) {
            if (key.includes('_')) {
              nutritionalProperties[key] = +(ingredient[key] * units).toFixed(1);
            } else {
              nutritionalProperties[key] = ingredient[key];
            }
          } else {
            nutritionalProperties[key] = 0;
          }
        }
      });
      // Número de porciones = número de unidades
      nutritionalProperties.portions = units;
      // Equivalencia en su unidad de medida
      nutritionalProperties.measurementUnits = ingredient.unitOfMeasurement?.symbol_es !== 'g' ?
        units * nutritionalProperties.suggestedQuantity : units * nutritionalProperties.netWeight;
      // Equivalencia en gramos
      nutritionalProperties.grams = ingredient.suggestedQuantity;
    } else {
      // De lo contrario, se hace la regla de 3
      // (por lo que equivale en gramos con la unidad de medida del ingrediente)
      // Por  el momento sólo aceptaría gramos y la unidad ligada del ingrediente
      const selectedMeasureUnit: IMeasurementUnit = measureUnits.find(mu => mu._id === selectedMeasureUnitId);
      if (selectedMeasureUnit?.symbol_es === 'g') {
        // Para gramos usar: 'netWeight'
        Object.keys(ingredient).forEach(key => {
          if (nutritionalProperties[key] !== undefined) {
            if (ingredient[key] !== -1) {
              if (key.includes('_')) {
                // antes tenia / nutritionalProperties.netWeight pero como no tenia valores, use el ingredient
                nutritionalProperties[key] = +((ingredient[key] * units) / ingredient.netWeight).toFixed(1);
              } else {
                nutritionalProperties[key] = ingredient[key];
              }
            } else {
              nutritionalProperties[key] = 0;
            }
          }
        });
        // Cálculo de las porciones
        // antes tenia / nutritionalProperties.netWeight pero como no tenia valores, use el ingredient
        nutritionalProperties.portions = +(units / ingredient.netWeight).toFixed(2);
        // Equivalencia en su unidad de medida
        nutritionalProperties.measurementUnits = +(units / (ingredient.netWeight / ingredient.suggestedQuantity)).toFixed(2);
        // Equivalencia en gramos
        nutritionalProperties.grams = units;

      } else if (selectedMeasureUnitId === ingredient?.unitOfMeasurementId) {
        // Para su unidad ligada usar: 'suggestedQuantity'
        Object.keys(ingredient).forEach(key => {
          if (nutritionalProperties[key] !== undefined) {
            if (ingredient[key] !== -1) {
              if (key.includes('_')) {
                // antes tenia / nutritionalProperties.suggestedQuantity pero como no tenia valores, use el ingredient
                nutritionalProperties[key] = +((ingredient[key] * units) / ingredient.suggestedQuantity).toFixed(1);
              } else {
                nutritionalProperties[key] = ingredient[key];
              }
            } else {
              nutritionalProperties[key] = 0;
            }
          }
        });
        // Cálculo de las porciones
        // antes tenia / nutritionalProperties.suggestedQuantity pero como no tenia valores, use el ingredient
        nutritionalProperties.portions = +(units / ingredient.suggestedQuantity).toFixed(2);
        // Equivalencia en su unidad de medida
        nutritionalProperties.measurementUnits = units;
        // Equivalencia en gramos
        nutritionalProperties.grams = +(units * ingredient.netWeight / ingredient.suggestedQuantity).toFixed(2);
      }
    }
    return cloneDeep(nutritionalProperties);
  }

  setZerosNutrients(): INutritionalProperties {
    return {
      carbohydrate_g: 0,
      grossWeight: 0,
      calcium_mg: 0,
      biotin_ug: 0,
      ascorbicAcid_mg: 0,
      chloride_mg: 0,
      choline_mg: 0,
      cholesterol_mg: 0,
      chromium_ug: 0,
      dietaryFiber_g: 0,
      energy_kcal: 0,
      copper_mg: 0,
      fluoride_ug: 0,
      ethanol_g: 0,
      folicAcid_ug: 0,
      glycemicIndex: 0,
      grams: 0,
      glycemicLoad: 0,
      heminicIron_mg: 0,
      folate_ug: 0,
      magnesium_mg: 0,
      manganese_mg: 0,
      netWeight: 0,
      measurementUnits: 0,
      iodine_ug: 0,
      monounsaturatedFats_g: 0,
      niacin_mg: 0,
      noHeminicIron_mg: 0,
      pantothenicAcid_mg: 0,
      phosphorus_mg: 0,
      polyunsaturatedFats_g: 0,
      portions: 0,
      potassium_mg: 0,
      protein_g: 0,
      riboflavin_mg: 0,
      saturatedFats_g: 0,
      selenium_ug: 0,
      suggestedQuantity: 0,
      sugar_g: 0,
      thiamine_mg: 0,
      totalFat_g: 0,
      transFats_g: 0,
      vitaminA_ug: 0,
      sodium_mg: 0,
      vitaminB6_mg: 0,
      vitaminB12_ug: 0,
      vitaminC_mg: 0,
      vitaminD_ug: 0,
      vitaminE_mg: 0,
      vitaminK_ug: 0,
      zinc_mg: 0
    };
  }

  nutrientsSummation(nutrients: INutritionalProperties[]): INutritionalProperties {
    const props: INutritionalProperties = this.setZerosNutrients();
    for (const nutrient of nutrients) {
      // tslint:disable-next-line:forin
      for (const property in props) {
        props[property] += nutrient[property] ?? 0;
      }
    }
    return props;
  }

  /**
   * convierte un tipo de medida a su equivalente en gramos
   * @param units
   * @param netWeight
   * @param suggestedQuantity
   * @returns
   */
  convertToGrams(units: number, netWeight: number = 0, suggestedQuantity: number = 0): number {
    return Math.floor((units * netWeight) / suggestedQuantity);
  }
}
